import { useState } from 'react'

import InformationCircleIcon from '@heroicons/react/outline/InformationCircleIcon'
import clsx from 'clsx'
import { Link } from 'react-router-dom'

import { EmailLog, EmailType } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-sdk/components'
import { useEmailLogs } from '@tribeplatform/react-sdk/hooks'
import { GridList } from '@tribeplatform/react-ui-kit/Layout'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { useDebounce } from '../../../utils/useDebounce'
import { Table } from '../../Table'
import { emailStatusMapping } from './constants'
import { EmailStatusModal } from './EmailStatusModal'

export const emailStatusRenderer = (emailLog: EmailLog) => {
  const emailStatus = emailStatusMapping[emailLog?.status]
  if (emailStatus) {
    const { className, title, description } = emailStatus
    if (new Date(emailLog?.createdAt) < new Date('2022-03-01')) return '-'

    return (
      <div>
        <Tooltip>
          <Tooltip.Trigger
            className={clsx('font-medium inline-block', className)}
          >
            {title}
          </Tooltip.Trigger>
          <Tooltip.Panel>
            <div>{description}</div>
            {emailLog?.reason ? (
              <div className="mt-2">Reason: {emailLog?.reason}</div>
            ) : null}
          </Tooltip.Panel>
        </Tooltip>
      </div>
    )
  }
  return '-'
}

const emailStatuses = [
  { value: null, text: 'All statuses' },
  ...Object.keys(emailStatusMapping).map(key => ({
    value: key,
    text: emailStatusMapping[key]?.title,
  })),
]

const emailTypes = [
  { value: null, text: 'All types' },
  {
    value: EmailType.EMAIL_VERIFICATION,
    text: 'Verification',
  },
  {
    value: EmailType.MEMBER_CONFIRMATION,
    text: 'Confirmation',
  },
  {
    value: EmailType.MEMBER_INVITATION,
    text: 'Invitation',
  },
  { value: EmailType.NOTIFICATION, text: 'Notification' },
  {
    value: EmailType.RESET_PASSWORD,
    text: 'Reset password',
  },
  { value: EmailType.UNKNOWN, text: 'Other' },
]

export const EmailLogs = () => {
  const [type, setType] = useState(null)
  const [status, setStatus] = useState(null)
  const [recipient, setRecipient] = useState('')
  const [emailStatusModal, setEmailStatusModal] = useState(false)
  const debouncedRecipient = useDebounce(recipient, 300)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useEmailLogs({
      variables: {
        limit: 30,
        types: type ? [type] : undefined,
        statuses: status ? [status] : undefined,
        recipient: debouncedRecipient,
        reverse: true,
      },
      useInfiniteQueryOptions: { keepPreviousData: true },
    })

  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      visible: false,
      width: 'md',
    },
    {
      Header: 'Recipient',
      accessor: 'recipient',
      visible: true,
      width: 'md',
    },
    {
      Header: 'Created at',
      accessor: 'createdAt',
      renderer: 'date',
      format: 'datetime',
      orderKey: 'createdAt',
      width: 'md',
      visible: true,
    },
    {
      Header: 'Updated at',
      accessor: 'updatedAt',
      orderKey: 'updatedAt',
      renderer: 'date',
      format: 'datetime',
      width: 'md',
      visible: false,
    },
    {
      Header: 'Type',
      accessor: 'type',
      width: 'md',
      visible: true,
      renderer: emailLog => {
        return (
          emailTypes?.find(emailType => emailType.value === emailLog.type)
            ?.text || '-'
        )
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      visible: true,
      width: 'sm',
      renderer: emailStatusRenderer,
    },
    {
      Header: 'Reason',
      accessor: 'reason',
      visible: false,
      width: 'xl',
      renderer: 'longtext',
    },
  ]

  return (
    <div className="w-full">
      <Form onSubmit={null} defaultValues={{ noun: '', timeframe: '168' }}>
        <GridList columns={3} className="mb-5 px-2">
          <GridList.Item>
            <Form.Input
              name="recipient"
              label="Recipient"
              placeholder="Search for email..."
              onChange={e => {
                setRecipient(e?.target?.value)
              }}
            />
          </GridList.Item>
          <GridList.Item>
            <Form.Select
              name="type"
              label="Email type"
              placeholder="All types"
              onChange={value => {
                setType(value)
              }}
              items={emailTypes}
            />
          </GridList.Item>
          <GridList.Item>
            <Form.Select
              name="status"
              label={
                <div className="items-center flex space-x-1">
                  <div>Email status</div>
                  <Link to="#" onClick={() => setEmailStatusModal(true)}>
                    <InformationCircleIcon className="w-4 h-4" />
                  </Link>
                </div>
              }
              placeholder="All statuses"
              onChange={value => {
                setStatus(value)
              }}
              items={emailStatuses}
            />
          </GridList.Item>
        </GridList>
      </Form>
      <Table
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        title="Email logs"
      />
      {emailStatusModal && (
        <EmailStatusModal
          open={emailStatusModal}
          onClose={() => setEmailStatusModal(false)}
        />
      )}
    </div>
  )
}
